import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import {
  getAllPricingLayerIds,
  createPricingFillColorExpression,
  PRICING_LAYER_ORDER,
  getPricingField,
  formatPricingValue,
  getPricingLayerDisplayName,
  getPricingIndex,
  createPricingVectorTileSource,
} from '../../../../molecules/AnalysisMap/utils/pricingLayerHelpers'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'
import { API_BASE_URL } from '../../../../../config'

interface UsePricingLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
}

/**
 * Hook to handle pricing choropleth layers (H3 hexagon vector tiles) and hover tooltips
 */
export const usePricingLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
}: UsePricingLayersProps) => {
  // Handle pricing choropleth layers (H3 hexagon vector tiles)
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    // Check if either purchase_market or rental_market section is active
    const activeSection = (otherMapOptions as any).activeSection
    const isPricingSection =
      activeSection === 'purchase_market' || activeSection === 'rental_market'
    const pricingLayerStates = (otherMapOptions as any).demographicsLayerStates // Reuse same prop for consistency

    if (!isPricingSection || !pricingLayerStates) {
      // Remove pricing sources and layers if not in pricing section
      let existingPricingLayers: string[] = []
      try {
        const style = map.current.getStyle()
        if (style?.layers) {
          existingPricingLayers = style.layers
            .filter(
              (layer) =>
                layer.id.startsWith('purchase-market-') ||
                layer.id.startsWith('rental-market-')
            )
            .map((layer) => layer.id)
        }
      } catch (error) {
        // Style not loaded yet, skip cleanup
        return
      }

      existingPricingLayers.forEach((layerId) => {
        if (map.current!.getLayer(layerId)) {
          try {
            map.current!.removeLayer(layerId)
          } catch (error) {
            console.warn(`Failed to remove pricing layer ${layerId}:`, error)
          }
        }
      })

      // Remove all pricing vector tile sources (one per layer)
      getAllPricingLayerIds().forEach((layerId) => {
        const sourceId = `${layerId}-source`
        if (map.current!.getSource(sourceId)) {
          try {
            map.current!.removeSource(sourceId)
          } catch (error) {
            console.warn(`Failed to remove pricing source ${sourceId}:`, error)
          }
        }
      })

      return
    }

    // Pricing section is active - add vector tile sources and layers
    // Unlike demographics/employment which use one shared source, pricing layers each need their own source
    // because they query different Elasticsearch indices
    const layerIds = getAllPricingLayerIds()

    layerIds.forEach((layerId: string) => {
      const layerConfig = getLayerConfig(layerId)
      const layerState = pricingLayerStates[layerId] || {
        enabled: layerConfig?.defaultEnabled || false,
        opacity: layerConfig?.defaultOpacity || 70,
      }

      const index = getPricingIndex(layerId)
      if (!index) {
        console.warn(`No index mapping found for pricing layer: ${layerId}`)
        return
      }

      const sourceId = `${layerId}-source`

      // Add vector tile source if it doesn't exist (one per layer)
      if (!map.current!.getSource(sourceId)) {
        const sourceConfig = createPricingVectorTileSource(API_BASE_URL, index)
        map.current!.addSource(sourceId, sourceConfig)
      }

      if (!map.current!.getLayer(layerId)) {
        // Add new fill layer

        const fillColorExpression = createPricingFillColorExpression(
          layerId,
          layerConfig
        )

        map.current!.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId, // Use layer-specific source
          'source-layer': 'aggs', // H3 hexagon aggregations layer from Elasticsearch searchMvt
          layout: {
            visibility: layerState.enabled ? 'visible' : 'none',
          },
          paint: {
            'fill-outline-color': 'transparent',
            'fill-color': fillColorExpression,
            'fill-opacity': layerState.opacity / 100,
          },
        })
      } else {
        // Update existing layer
        map.current!.setLayoutProperty(
          layerId,
          'visibility',
          layerState.enabled ? 'visible' : 'none'
        )
        map.current!.setPaintProperty(
          layerId,
          'fill-opacity',
          layerState.opacity / 100
        )
      }
    })

    // Apply Z-ordering to ensure proper layer stacking
    PRICING_LAYER_ORDER.forEach((layerId: string, index: number) => {
      if (map.current!.getLayer(layerId) && index > 0) {
        const previousLayerId = PRICING_LAYER_ORDER[index - 1]
        if (map.current!.getLayer(previousLayerId)) {
          try {
            map.current!.moveLayer(layerId, previousLayerId)
          } catch (error) {
            // Layer ordering might fail if layers don't exist yet, that's ok
          }
        }
      }
    })
  }, [mapLoaded, styleLoadedCount, otherMapOptions])

  // Setup hover tooltips for pricing layers
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    // Get all enabled pricing layers
    const enabledPricingLayers = getAllPricingLayerIds().filter((layerId) => {
      const layerState = otherMapOptions.demographicsLayerStates?.[layerId]
      return layerState?.enabled && map.current!.getLayer(layerId)
    })

    if (enabledPricingLayers.length === 0) return

    // Find the topmost visible layer (based on Z-order)
    const topmostLayer = PRICING_LAYER_ORDER.slice()
      .reverse()
      .find((layerId) => enabledPricingLayers.includes(layerId))

    if (!topmostLayer) return

    const field = getPricingField(topmostLayer)
    if (!field) return

    // Handle mouse move to show tooltip
    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: [topmostLayer],
      })

      if (features.length > 0 && popup.current) {
        const feature = features[0]
        const value = feature.properties?.[field]
        const formattedValue = formatPricingValue(topmostLayer, value)
        const layerName = getPricingLayerDisplayName(topmostLayer)

        // Change cursor to pointer
        map.current!.getCanvas().style.cursor = 'pointer'

        // Show popup with formatted value
        popup.current
          .setLngLat(e.lngLat)
          .setHTML(
            `<div style="padding: 8px 12px; font-size: 13px;">
              <div style="font-weight: 600; margin-bottom: 4px;">${layerName}</div>
              <div>${formattedValue}</div>
            </div>`
          )
          .addTo(map.current!)
      } else {
        // Reset cursor
        map.current!.getCanvas().style.cursor = ''
        popup.current?.remove()
      }
    }

    // Handle mouse leave to hide tooltip
    const handleMouseLeave = () => {
      map.current!.getCanvas().style.cursor = ''
      popup.current?.remove()
    }

    // Add event listeners
    map.current.on('mousemove', topmostLayer, handleMouseMove)
    map.current.on('mouseleave', topmostLayer, handleMouseLeave)

    // Cleanup
    return () => {
      if (map.current) {
        map.current.off('mousemove', topmostLayer, handleMouseMove)
        map.current.off('mouseleave', topmostLayer, handleMouseLeave)
      }
      popup.current?.remove()
    }
  }, [mapLoaded, otherMapOptions.demographicsLayerStates])
}
